/**
 * Dashboard — the operator's queue of active to-dos. The landing page
 * (ADR-0082). Three sections, sectioned by what each Task is about:
 * Operations / Firm Success / Misc.
 *
 * Slice C: read-only render of Mine.
 * Slice D (this commit): inline row expand → thread + seat-gated close-loop
 * buttons. The row is now an accordion: collapsed it shows the same compact
 * card; clicking it opens the thread (via `useTaskMessages`) and a context-
 * appropriate set of action buttons gated by the signed-in operator's email
 * vs the Task's `assigner` / `assignee`.
 *
 * Close-loop verbs map to wire actions like so:
 *   - Assignee: **Mark done** (PATCH `mark-done`, optional write-up note
 *     posted to the thread first), **Set commit** (PATCH `set-commit` with
 *     `committedBy`).
 *   - Assigner, when status === "done": **Acknowledge** (PATCH `clear`),
 *     **Send back** (PATCH `send-back`, optional reason posted to the
 *     thread first).
 *   - Assigner, any status: **Cancel** (PATCH `clear`, optional note posted
 *     to the thread first).
 *
 * Acknowledge and Cancel both hit the wire `clear` action — the UI verbs
 * disambiguate the operator's intent in the thread message, not the
 * audit log. Documented design call (Slice D pre-mortem in
 * `verification-log.md` (evicted 2026-08-29, #775 round 10)); the Acknowledge verb (renamed from "Confirm" in
 * Slice J / ADR-0092) signals that a done row is the assigner's to
 * consume. Server-side seat guard is the source of truth; the UI gating
 * is belt-and-suspenders.
 *
 * Following slices:
 *   - Slice E: "+ New task" modal with a unified search picker.
 *   - Slice F: Mine / peer sub-tab toggle.
 *
 * Seated outside the roster (#1024): on the Mine view, and only for the
 * system owner, a band below the three sections lists every live task seated
 * on an address off the roster (someone who left, a synthetic identity) — the
 * rows no Mine pane shows. A row there offers the owner the verbs of every
 * seat that is his or is off the roster (the server lets the owner act from
 * any seat, #959); a watch offers Un-pin, the Surface's `clear`.
 *
 * Empty state: when mineCount is 0, render a friendly nothing-on-your-plate
 * message rather than three empty section headers.
 */

import { useEffect, useRef, useState } from "react";
import {
  DASHBOARD_SECTION_LABELS,
  dateInPT,
  isOutsideRoster,
  isSystemOwner,
  operatorByEmail,
  peersOf,
  todayInPT,
  type DashboardRow,
  type DashboardSection,
  type Operator,
  type TaskMessage,
} from "@command-center/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { fmtRelativeDay, fmtTimeAgo, initials } from "@/lib/format";
import {
  useDashboard,
  useDashboardCount,
  useMarkAllSeen,
  useMarkTaskSeen,
  useOutsideRoster,
} from "@/lib/use-dashboard";
import { useMe } from "@/lib/use-me";
import { useViewerSelection } from "@/lib/use-viewer-selection";
import {
  useAddMessage,
  useTaskAction,
  useTaskMessages,
} from "@/lib/use-surface-tasks";
import { CreateTaskModal } from "./create-task-modal";
import { DashboardNav } from "@/components/dashboard-nav";
import { Link } from "@tanstack/react-router";

const SECTION_ORDER: readonly DashboardSection[] = [
  "operations",
  "client-success",
  "misc",
];

/** Components that aren't yet a producer of Dashboard rows. Slice G renders
 *  these as dormant section headers so the operator sees the full target
 *  shape — what's checked vs. what's missing — even before the components
 *  light up. They have no rows and no empty-state copy that implies they
 *  might have rows; they're labels for future expansion. */
const DORMANT_SECTIONS: readonly string[] = [
  "Engineering",
  "Marketing",
  "Finance",
  "Legal",
];

/** Per-section empty-state copy. Persistent section headers (Slice G):
 *  the page tells you what was checked even when nothing was found. */
const SECTION_EMPTY_COPY: Record<DashboardSection, string> = {
  operations: "No Operations to-dos.",
  "client-success": "No Firm Success to-dos.",
  misc: "No Misc to-dos.",
};

/** The calendar day (YYYY-MM-DD) of a row's `created` value, which is a
 *  Task's created instant, a date-only label (a review row's Pending Comms
 *  Submitted At), or empty. A label is its own day; an instant is its
 *  Pacific day; anything else is the empty string. */
function dayOf(value: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  return dateInPT(value) ?? "";
}

/** Days between two day labels (YYYY-MM-DD). Used for the age badge and the
 *  Overdue chip. Negative ages clamp to 0 (clock skew defensiveness). */
function daysSince(iso: string, todayYmd: string): number {
  const created = new Date(`${iso.slice(0, 10)}T00:00:00Z`).getTime();
  const today = new Date(`${todayYmd}T00:00:00Z`).getTime();
  if (Number.isNaN(created) || Number.isNaN(today)) return 0;
  const days = Math.floor((today - created) / 86_400_000);
  return days < 0 ? 0 : days;
}

/** Compact age label: "today" (0d), "Nd" (<30), "Nmo" (<365), "Nyr". */
function ageLabel(days: number): string {
  if (days === 0) return "today";
  if (days < 30) return `${days}d`;
  if (days < 365) return `${Math.floor(days / 30)}mo`;
  return `${Math.floor(days / 365)}yr`;
}

type AttentionLevel = "red" | "amber" | "none";
type Attention = { level: AttentionLevel; label: string | null };

const NO_ATTENTION: Attention = { level: "none", label: null };

/**
 * ONE attention channel (ADR-0104). Date urgency and priority feed the same
 * derivation, and the chip names the reason. Red = on fire (overdue, or
 * Urgent priority); amber = act soon (due today/tomorrow, commit missed,
 * High priority). Everything else renders quiet — Normal/Low and far-off
 * dates earn no ink. Done rows wear none: they're awaiting acknowledge,
 * not action.
 */
function attentionFor(row: DashboardRow, today: string): Attention {
  if (row.status === "done") return NO_ATTENTION;
  if (row.dueBy !== null && row.dueBy < today) {
    const days = daysSince(row.dueBy, today);
    return {
      level: "red",
      label: days <= 1 ? "Overdue 1 day" : `Overdue ${days} days`,
    };
  }
  if (row.priority === "Urgent") return { level: "red", label: "Urgent" };
  if (row.dueBy === today) return { level: "amber", label: "Due today" };
  if (row.committedBy !== null && row.committedBy < today) {
    return { level: "amber", label: "Commit missed" };
  }
  if (row.dueBy !== null && fmtRelativeDay(row.dueBy, today) === "tomorrow") {
    return { level: "amber", label: "Due tomorrow" };
  }
  if (row.priority === "High") return { level: "amber", label: "High" };
  return NO_ATTENTION;
}

interface SectionStats {
  total: number;
  /** Rows wearing red or amber attention. */
  attention: number;
  /** Unseen rows — the section's share of the "new" badge. */
  fresh: number;
  awaitingMyConfirm: number;
}

/** Slice J (ADR-0092): hoist done-by-other rows out of each section into a
 *  pile-at-top. The filter applies the row's `assigner` against the API
 *  viewer (Mine: signed-in operator; Peer: the peer email) so the surface
 *  always reflects "what THAT operator owes acknowledge on." Returns:
 *    - `pile` — owed-acknowledge rows, paired with their origin section
 *      (kept so the row's identity glyph picks the right shape).
 *    - `filteredSections` — sections with hoisted rows removed.
 *  Pure; no side effects; safe to call on every render. */
function partitionPile(
  sections: Record<DashboardSection, readonly DashboardRow[]>,
  viewer: string,
): {
  pile: { row: DashboardRow; originSection: DashboardSection }[];
  filteredSections: Record<DashboardSection, DashboardRow[]>;
} {
  const pile: { row: DashboardRow; originSection: DashboardSection }[] = [];
  const filteredSections: Record<DashboardSection, DashboardRow[]> = {
    operations: [],
    "client-success": [],
    misc: [],
  };
  const lowerViewer = viewer.trim().toLowerCase();
  for (const section of SECTION_ORDER) {
    for (const row of sections[section]) {
      const isOwedAcknowledge =
        row.status === "done" &&
        lowerViewer !== "" &&
        row.assigner.trim().toLowerCase() === lowerViewer;
      if (isOwedAcknowledge) {
        pile.push({ row, originSection: section });
      } else {
        filteredSections[section].push(row);
      }
    }
  }
  return { pile, filteredSections };
}

/** Pure: per-section sub-counts. `awaitingMyConfirm` is computed only when
 *  the viewer is the signed-in operator AND there are done-rows assignered
 *  by them — on the peer tab the sub-count is meaningless noise. After
 *  Slice J's hoist this number will typically be 0 (those rows live in the
 *  pile, not the section); the field stays for defense and for the peer-
 *  view edge where a future change could re-introduce in-section dones. */
function statsFor(
  rows: readonly DashboardRow[],
  today: string,
  signedInEmail: string,
  countAwaitingMyConfirm: boolean,
): SectionStats {
  let attention = 0;
  let fresh = 0;
  let awaitingMyConfirm = 0;
  const lowerSelf = signedInEmail.trim().toLowerCase();
  for (const row of rows) {
    if (attentionFor(row, today).level !== "none") attention++;
    if (!row.seen) fresh++;
    if (
      countAwaitingMyConfirm &&
      row.status === "done" &&
      row.assigner !== null &&
      row.assigner.trim().toLowerCase() === lowerSelf
    ) {
      awaitingMyConfirm++;
    }
  }
  return { total: rows.length, attention, fresh, awaitingMyConfirm };
}

export function DashboardPage() {
  const me = useMe();
  const signedInEmail = me.data?.email ?? "";
  const { viewer, viewerForApi, isMine, setViewer } =
    useViewerSelection(signedInEmail);
  // `showSnoozed` is deliberately local state, NOT URL-persisted (unlike
  // `viewer`, which lives in the URL via `useViewerSelection`). Snooze is
  // "park until later, out of sight by default" — the toggle is a brief
  // peek to bring something back, not a view mode the operator lives in.
  // Refresh and navigation reset to the default truthful view; that's the
  // intended shape (Slice H / ADR-0088).
  const [showSnoozed, setShowSnoozed] = useState(false);
  const query = useDashboard(viewerForApi, { includeSnoozed: showSnoozed });
  const [createOpen, setCreateOpen] = useState(false);
  const markAllSeen = useMarkAllSeen();
  const today = todayInPT();
  // #1024: the owner's outside-roster band — Mine view only, owner only.
  const showOutsideRoster = isMine && isSystemOwner(signedInEmail);
  const outsideRoster = useOutsideRoster(showOutsideRoster);

  const peers = peersOf(signedInEmail);
  const viewerOperator = operatorByEmail(viewer);
  const viewerDisplay = viewerOperator?.displayName ?? viewer;

  const header = (
    <DashboardHeader
      signedInEmail={signedInEmail}
      viewer={viewer}
      peers={peers}
      isMine={isMine}
      onSelectMine={() => setViewer(signedInEmail)}
      onSelectPeer={(email) => setViewer(email)}
      onNewTask={() => setCreateOpen(true)}
      showSnoozed={showSnoozed}
      onToggleSnoozed={() => setShowSnoozed((v) => !v)}
      // Mark-all-seen is Mine-only (Slice K): the endpoint writes for the
      // authenticated actor, so offering it on the Peer tab would mislead.
      newCount={isMine ? (query.data?.newCount ?? 0) : 0}
      onMarkAllSeen={() => markAllSeen.mutate()}
    />
  );
  const modal =
    signedInEmail !== "" ? (
      <CreateTaskModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        viewerEmail={signedInEmail}
      />
    ) : null;

  if (query.isLoading) {
    return (
      <Shell>
        {header}
        <div className="p-8 text-sm text-plane-muted" data-testid="dashboard-loading">
          Loading…
        </div>
        {modal}
      </Shell>
    );
  }

  if (query.isError) {
    return (
      <Shell>
        {header}
        <div className="p-8 text-sm text-error" data-testid="dashboard-error">
          Couldn't load the Dashboard. It will retry automatically.
        </div>
        {modal}
      </Shell>
    );
  }

  const data = query.data;
  const sections = data?.sections ?? {
    operations: [],
    "client-success": [],
    misc: [],
  };
  // Slice J (ADR-0092): hoist done-by-other rows OUT of their original
  // sections INTO a pile-at-top "Awaiting your acknowledge" section. Pure
  // client-side derivation — no wire-shape change. The filter uses `viewer`
  // (the resolved API viewer), so on Peer view an operator sees the peer's
  // owed-acknowledge surface; the Acknowledge button is gated by the existing
  // assigner-seat check in ExpandedRow, so the pile in Peer view renders
  // read-only. Hoisting (not double-rendering) preserves Slice G's row-as-
  // row "one row, one place" principle and avoids the ack-clears-pile-but-
  // row-stays-in-section bug.
  const { pile, filteredSections } = partitionPile(sections, viewer);
  const allEmptyHint =
    data && data.mineCount === 0
      ? isMine
        ? "Nothing on your plate yet — create one to get started, or check your peer's tab."
        : `Nothing on ${viewerDisplay}'s plate right now.`
      : null;

  return (
    <Shell>
      {header}
      <div className="space-y-6" data-testid="dashboard">
        {pile.length > 0 ? (
          <section
            data-testid="dashboard-pile"
            aria-labelledby="section-acknowledge-heading"
            className="rounded border border-foreground/20 bg-muted/40 p-3"
          >
            <PileHeading count={pile.length} />
            <div className="space-y-2">
              {pile.map(({ row }) => (
                <DashboardRowCard
                  key={row.id}
                  row={row}
                  viewerEmail={signedInEmail}
                  attention={NO_ATTENTION}
                  today={today}
                  canMarkSeen={isMine}
                />
              ))}
            </div>
          </section>
        ) : null}
        {SECTION_ORDER.map((section) => {
          const rows = filteredSections[section];
          const stats = statsFor(rows, today, signedInEmail, isMine);
          return (
            <section
              key={section}
              data-testid={`dashboard-section-${section}`}
              aria-labelledby={`section-${section}-heading`}
            >
              <SectionHeading section={section} stats={stats} />
              {rows.length === 0 ? (
                <p
                  className="text-xs italic text-plane-muted"
                  data-testid={`section-empty-${section}`}
                >
                  {SECTION_EMPTY_COPY[section]}
                </p>
              ) : (
                <div className="space-y-2">
                  {rows.map((row) =>
                    row.kind === "review" ? (
                      <ReviewRowCard key={row.id} row={row} />
                    ) : (
                      <DashboardRowCard
                        key={row.id}
                        row={row}
                        viewerEmail={signedInEmail}
                        attention={attentionFor(row, today)}
                        today={today}
                        canMarkSeen={isMine}
                      />
                    ),
                  )}
                </div>
              )}
            </section>
          );
        })}
        {showOutsideRoster ? (
          <OutsideRosterBand
            query={outsideRoster}
            signedInEmail={signedInEmail}
            today={today}
          />
        ) : null}
        <DormantSections />
        {allEmptyHint !== null ? (
          <p
            className="pt-2 text-center text-xs text-plane-muted"
            data-testid="dashboard-empty-hint"
          >
            {allEmptyHint}
          </p>
        ) : null}
      </div>
      {modal}
    </Shell>
  );
}

/** The system owner's band of tasks seated outside the roster (#1024). Renders
 *  below the three sections on the Mine view; nothing while loading, one line
 *  on error, and its empty state when no task is seated off the roster. Each
 *  row is a DashboardRowCard that acts for the off-roster seats; no mark-seen
 *  (these rows carry no newness). */
function OutsideRosterBand({
  query,
  signedInEmail,
  today,
}: {
  query: ReturnType<typeof useOutsideRoster>;
  signedInEmail: string;
  today: string;
}) {
  if (query.isLoading) return null;
  const rows = query.data?.rows ?? [];
  return (
    <section
      data-testid="dashboard-outside-roster"
      aria-labelledby="section-outside-roster-heading"
    >
      <div
        className="mb-2 flex flex-wrap items-baseline gap-x-3 gap-y-1"
        data-testid="section-heading-outside-roster"
      >
        <h2
          id="section-outside-roster-heading"
          className="text-sm font-sans font-semibold uppercase tracking-wider text-plane-muted"
        >
          Seated outside the roster
        </h2>
        {query.isError ? null : (
          <span
            className="text-xs text-plane-muted"
            data-testid="section-count-outside-roster"
          >
            {query.data?.count ?? 0} total
          </span>
        )}
      </div>
      {query.isError ? (
        <p
          className="text-xs italic text-error"
          data-testid="outside-roster-error"
        >
          Couldn't load these tasks. It will retry automatically.
        </p>
      ) : rows.length === 0 ? (
        <p
          className="text-xs italic text-plane-muted"
          data-testid="section-empty-outside-roster"
        >
          No task is seated outside the roster.
        </p>
      ) : (
        <div className="space-y-2">
          {rows.map((row) => (
            <DashboardRowCard
              key={row.id}
              row={row}
              viewerEmail={signedInEmail}
              attention={attentionFor(row, today)}
              today={today}
              canMarkSeen={false}
              actForOutsideSeats
            />
          ))}
        </div>
      )}
    </section>
  );
}

/** Dormant section headers — Engineering / Marketing / Finance / Legal.
 *  Rendered grey/inert below the live sections so the operator sees the
 *  full target shape. No empty-state copy implying rows might arrive; the
 *  label itself is the affordance. */
function DormantSections() {
  return (
    <section
      className="space-y-1 border-t border-dashed border-border/60 pt-4"
      data-testid="dashboard-dormant"
      aria-label="Dormant components"
    >
      <p className="text-[10px] uppercase tracking-wider text-plane-muted/70">
        Other components
      </p>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {DORMANT_SECTIONS.map((label) => (
          <span
            key={label}
            data-testid={`section-dormant-${label.toLowerCase()}`}
            className="text-xs font-medium uppercase tracking-wider text-plane-muted/50"
          >
            {label}
          </span>
        ))}
      </div>
    </section>
  );
}

function DashboardHeader({
  signedInEmail,
  viewer,
  peers,
  isMine,
  onSelectMine,
  onSelectPeer,
  onNewTask,
  showSnoozed,
  onToggleSnoozed,
  newCount,
  onMarkAllSeen,
}: {
  signedInEmail: string;
  /** The currently-read queue's email — which peer segment renders active. */
  viewer: string;
  peers: readonly Operator[];
  isMine: boolean;
  onSelectMine: () => void;
  onSelectPeer: (email: string) => void;
  onNewTask: () => void;
  showSnoozed: boolean;
  onToggleSnoozed: () => void;
  /** Unseen rows on the viewer's queue. The page passes 0 on a peer tab —
   *  Mark-all-seen writes for the authenticated actor only (Slice K). */
  newCount: number;
  onMarkAllSeen: () => void;
}) {
  const showToggle = peers.length > 0 && signedInEmail !== "";
  return (
    <div className="mb-4 flex items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        <h1 className="text-xl tracking-tight">Dashboard</h1>
        {showToggle ? (
          <ViewerToggle
            isMine={isMine}
            viewer={viewer}
            peers={peers}
            onSelectMine={onSelectMine}
            onSelectPeer={onSelectPeer}
          />
        ) : null}
      </div>
      <div className="flex items-center gap-2">
        {newCount > 0 ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={onMarkAllSeen}
            data-testid="mark-all-seen"
          >
            Mark all seen
          </Button>
        ) : null}
        <label
          className="flex cursor-pointer select-none items-center gap-1 text-xs text-plane-muted"
          data-testid="show-snoozed-toggle"
        >
          <input
            type="checkbox"
            checked={showSnoozed}
            onChange={onToggleSnoozed}
            className="h-3 w-3"
            data-testid="show-snoozed-checkbox"
          />
          Show snoozed
        </label>
        <Button size="sm" onClick={onNewTask} data-testid="dashboard-new-task">
          + New task
        </Button>
      </div>
    </div>
  );
}

/** Viewer picker: one segment for Mine, then one per peer. Does double duty —
 *  it switches the viewer AND reports each operator's current load. Counts come
 *  from /api/dashboard/count, each keyed by viewer: Mine uses the no-arg call
 *  (same query the sidebar badge consumes, so it shares cache); each peer
 *  fetches with ?viewer=.
 *
 *  #708: this was a two-segment Mine/Theirs switch built on a `pickPeer` helper
 *  that returned the FIRST roster entry that wasn't you. With a third operator
 *  that silently made one person's queue unreachable — no error, just a tab
 *  with no route to it. Rendering the whole peer set removes the two-person
 *  assumption. The shape stays segmented (rather than a dropdown) because the
 *  per-operator count is the point of the control; if the roster ever outgrows
 *  a handful of segments, that is the moment to collapse it into a menu. */
function ViewerToggle({
  isMine,
  viewer,
  peers,
  onSelectMine,
  onSelectPeer,
}: {
  isMine: boolean;
  viewer: string;
  peers: readonly Operator[];
  onSelectMine: () => void;
  onSelectPeer: (email: string) => void;
}) {
  const mineCountQuery = useDashboardCount();
  const lowerViewer = viewer.trim().toLowerCase();

  return (
    <div
      role="tablist"
      aria-label="Dashboard viewer"
      data-testid="viewer-toggle"
      className="inline-flex overflow-hidden rounded-md border border-border bg-muted/30"
    >
      <ToggleSegment
        active={isMine}
        onClick={onSelectMine}
        label="Mine"
        count={mineCountQuery.data?.count}
        testid="viewer-toggle-mine"
      />
      {peers.map((op) => (
        <PeerSegment
          key={op.email}
          operator={op}
          active={!isMine && op.email.toLowerCase() === lowerViewer}
          onSelect={() => onSelectPeer(op.email)}
        />
      ))}
    </div>
  );
}

/** One peer's segment. Split out because each peer needs its own
 *  `useDashboardCount` call and hooks cannot be called from inside a loop
 *  in the parent.
 *
 *  Testid keys off the EMAIL, not the display name. Display names are free
 *  text and neither unique nor slug-safe — a second "Chris" would emit two
 *  identical testids and make `findByTestId` throw, and "Chris R." would emit
 *  an awkward one. Email is the same value the React key uses and is the only
 *  field guaranteed distinct.
 *
 *  Cadence: the active PEER segment polls fast, inactive peers slow. Without
 *  that, every peer would hold a 30s timer in every open tab, forever, for
 *  queues nobody is reading.
 *
 *  Mine is exempt and stays fast unconditionally — it shares the `"mine"`
 *  query key with the always-mounted sidebar badge, which wants the fast
 *  cadence regardless of which segment is being read. So "only the active
 *  segment is fast" is true of peers, not of the control as a whole; the
 *  steady-state cost is 1 fast + 1 fast + (N-2) slow, not 1 fast. */
function PeerSegment({
  operator,
  active,
  onSelect,
}: {
  operator: Operator;
  active: boolean;
  onSelect: () => void;
}) {
  const countQuery = useDashboardCount(operator.email, { watched: active });
  return (
    <ToggleSegment
      active={active}
      onClick={onSelect}
      label={`${operator.displayName}'s`}
      count={countQuery.data?.count}
      testid={`viewer-toggle-peer-${operator.email.toLowerCase()}`}
    />
  );
}

function ToggleSegment({
  active,
  onClick,
  label,
  count,
  testid,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number | undefined;
  testid: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      data-testid={testid}
      data-active={active ? "true" : "false"}
      className={cn(
        "flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors",
        active
          ? // text-foreground (not the candidate-swapped plane ink): the wash is
            // light in EVERY ground candidate, so the label needs wash-safe ink.
            "bg-active-wash text-foreground ring-1 ring-inset ring-active-edge/50"
          : "text-plane-muted hover:text-plane-foreground",
      )}
    >
      <span>{label}</span>
      {typeof count === "number" ? (
        <span
          data-testid={`${testid}-count`}
          className={cn(
            "rounded-full px-1.5 text-[10px] font-semibold leading-none",
            active ? "bg-active-edge/15 text-active-fg" : "bg-plane-muted/15",
          )}
        >
          {count}
        </span>
      ) : null}
    </button>
  );
}

/** Pile-at-top heading — "Awaiting your acknowledge (N)". Slice J / ADR-0092.
 *  Visually distinct container (rounded panel on the parent <section>) and a
 *  named id so the parent's `aria-labelledby` resolves. The pile section is
 *  its own beast — it doesn't share SectionHeading's stats shape because the
 *  rows inside are all by definition done-by-other (no overdue, no
 *  awaiting-confirm sub-counts). */
function PileHeading({ count }: { count: number }) {
  return (
    <div
      className="mb-2 flex flex-wrap items-baseline gap-x-3 gap-y-1"
      data-testid="section-heading-acknowledge"
    >
      <h2
        id="section-acknowledge-heading"
        className="text-sm font-sans font-semibold uppercase tracking-wider text-plane-foreground"
      >
        Awaiting your acknowledge
      </h2>
      <span
        className="text-xs text-plane-muted"
        data-testid="section-count-acknowledge"
      >
        {count}
      </span>
    </div>
  );
}

function SectionHeading({
  section,
  stats,
}: {
  section: DashboardSection;
  stats: SectionStats;
}) {
  return (
    <div
      className="mb-2 flex flex-wrap items-baseline gap-x-3 gap-y-1"
      data-testid={`section-heading-${section}`}
    >
      <h2
        id={`section-${section}-heading`}
        className="text-sm font-sans font-semibold uppercase tracking-wider text-plane-muted"
      >
        {DASHBOARD_SECTION_LABELS[section]}
      </h2>
      <span
        className="text-xs text-plane-muted"
        data-testid={`section-count-${section}`}
      >
        {stats.total} total
      </span>
      {stats.fresh > 0 ? (
        <span
          className="text-xs font-semibold text-cta"
          data-testid={`section-new-${section}`}
        >
          {stats.fresh} new
        </span>
      ) : null}
      {stats.attention > 0 ? (
        <span
          className="text-xs font-medium text-error"
          data-testid={`section-attention-${section}`}
        >
          {stats.attention} need{stats.attention === 1 ? "s" : ""} attention
        </span>
      ) : null}
      {stats.awaitingMyConfirm > 0 ? (
        <span
          className="text-xs font-medium text-plane-foreground"
          data-testid={`section-awaiting-confirm-${section}`}
        >
          {stats.awaitingMyConfirm} awaiting your confirm
        </span>
      ) : null}
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <section className="mx-auto max-w-4xl p-6">
      <DashboardNav active="tasks" />
      {children}
    </section>
  );
}

/** Case-insensitive email equality. */
function sameEmail(a: string | null, b: string): boolean {
  return (
    b !== "" &&
    a !== null &&
    a.trim().toLowerCase() === b.trim().toLowerCase()
  );
}

/** How long a row must sit on screen before view-newness clears. */
const VIEW_DWELL_MS = 2000;

/**
 * View-to-clear (ADR-0104). Created/status newness clears after the row has
 * been visible for ~2s — the headline IS the information, so seeing it is
 * reading it. (Message newness never view-clears; reading a reply requires
 * the expand, which keeps ADR-0099's expand-mark.) IntersectionObserver with
 * a dwell timer; no-op where the API is missing (jsdom) — tests pin the
 * expand path, the live walk pins this one.
 */
function useMarkSeenOnView(enabled: boolean, onSeen: () => void) {
  const ref = useRef<HTMLDivElement | null>(null);
  const onSeenRef = useRef(onSeen);
  onSeenRef.current = onSeen;
  useEffect(() => {
    const el = ref.current;
    if (!enabled || el === null) return;
    if (typeof IntersectionObserver === "undefined") return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            timer ??= setTimeout(() => {
              observer.disconnect();
              onSeenRef.current();
            }, VIEW_DWELL_MS);
          } else if (timer !== null) {
            clearTimeout(timer);
            timer = null;
          }
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(el);
    return () => {
      if (timer !== null) clearTimeout(timer);
      observer.disconnect();
    };
  }, [enabled]);
  return ref;
}

/**
 * One row card (ADR-0104 redesign). The collapsed card answers, in order:
 * what's new since I last looked (unread tint + bold title + a line saying
 * WHY — "New from Chris" / a reply snippet / a status flip), what's on
 * fire (one attention chip + stripe, reason named), and what this is (title
 * 15px semibold; entity + instruction preview on the support line). Default
 * states wear nothing: no OPEN chip, no NORMAL chip, no self-assignment, no
 * age. Full tracking detail (provenance, both dates, priority) lives in the
 * expanded view with the thread.
 */
/**
 * A DERIVED review notice (ADR-0115) — a firm's pending client-email change.
 * Not a Task: no expand, no thread, no mark-seen / close-loop. A simple
 * attention card with a deep-link to the firm's Client-Email Review subpage;
 * it self-heals (vanishes from the next dashboard build) once approved or the
 * firm cancels.
 */
function ReviewRowCard({ row }: { row: DashboardRow }) {
  return (
    <Card
      className="border-l-4 border-l-cta bg-cta/5 text-sm"
      data-testid={`dashboard-row-${row.id}`}
      data-kind="review"
    >
      <div className="flex items-start gap-3 p-3">
        <ActorAvatar email={row.assigner} />
        <div className="min-w-0 flex-1 space-y-0.5">
          <div
            className="truncate text-[15px] font-medium leading-snug text-foreground"
            data-testid={`row-title-${row.id}`}
          >
            {row.title}
          </div>
          {row.instruction !== null && row.instruction.trim() !== "" ? (
            <p
              className="text-[13px] text-muted-foreground"
              data-testid={`row-instruction-preview-${row.id}`}
            >
              {row.instruction}
            </p>
          ) : null}
        </div>
        {row.aboutHref !== null ? (
          <Link
            to={row.aboutHref}
            className="shrink-0 self-center rounded-md border px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-accent"
            data-testid={`review-row-link-${row.id}`}
          >
            Review
          </Link>
        ) : null}
      </div>
    </Card>
  );
}

function DashboardRowCard({
  row,
  viewerEmail,
  attention,
  today,
  canMarkSeen,
  actForOutsideSeats = false,
}: {
  row: DashboardRow;
  viewerEmail: string;
  attention: Attention;
  today: string;
  /** True only on the Mine view (Slice K) — mark-seen writes for the
   *  authenticated actor, so the Peer tab renders newness read-only. */
  canMarkSeen: boolean;
  /** #1024: the row sits in the owner's outside-roster band, so the expanded
   *  row also offers the verbs of every seat that is off the roster. */
  actForOutsideSeats?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const markSeen = useMarkTaskSeen();
  // One mark per card instance, whichever path fires first (view-dwell or
  // expand). Best-effort: a failed write leaves the newness for the next
  // look — never block the interaction.
  const markedRef = useRef(false);
  const isUnseen = !row.seen;
  const newKind = row.newActivity?.kind ?? null;
  const fireMarkSeen = () => {
    if (markedRef.current) return;
    markedRef.current = true;
    markSeen.mutate({ id: row.id });
  };
  const viewRef = useMarkSeenOnView(
    canMarkSeen && isUnseen && newKind !== "message",
    fireMarkSeen,
  );
  const toggleExpanded = () => {
    // Side effect outside the setExpanded updater (updaters must stay pure —
    // StrictMode double-invokes them). `!expanded` = this toggle is opening.
    if (!expanded && canMarkSeen && isUnseen) fireMarkSeen();
    setExpanded((v) => !v);
  };
  const stripeClass =
    attention.level === "red"
      ? "border-l-4 border-l-red-600"
      : attention.level === "amber"
      ? "border-l-4 border-l-amber-500"
      : "";
  // Whose row is this, at a glance: the operator whose activity made it new,
  // else the assigner. Their initials wear their color (ActorAvatar).
  const actorEmail = row.newActivity?.actor ?? row.assigner;
  // Quiet rows recede: Low priority with nothing new and nothing urgent.
  const quiet =
    row.priority === "Low" && !isUnseen && attention.level === "none";
  return (
    <Card
      ref={viewRef}
      className={cn(
        "text-sm",
        stripeClass,
        isUnseen && "border-cta/40 bg-cta/5",
        quiet && "opacity-70",
      )}
      data-testid={`dashboard-row-${row.id}`}
      data-attention={attention.level}
      data-unseen={isUnseen}
    >
      {/* Whole-row click target — a div with role=button (a Link nests
          inside for the entity affordance; a Link inside a button is invalid
          HTML). The Link uses stopPropagation so clicking the entity
          navigates without also toggling the expand state. */}
      <div
        role="button"
        tabIndex={0}
        onClick={toggleExpanded}
        onKeyDown={(e) => {
          // Only handle Enter/Space when the row div itself is focused —
          // keydown bubbling from the nested Link must not be cancelled.
          if (e.target !== e.currentTarget) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleExpanded();
          }
        }}
        aria-expanded={expanded}
        className="flex w-full cursor-pointer items-start gap-3 p-3 text-left"
        data-testid={`dashboard-row-${row.id}-toggle`}
      >
        <ActorAvatar email={actorEmail} />
        <div className="min-w-0 flex-1 space-y-0.5">
          <div
            className={cn(
              "truncate text-[15px] leading-snug text-foreground",
              isUnseen ? "font-bold" : "font-medium",
            )}
            data-testid={`row-title-${row.id}`}
          >
            {row.title.trim() !== "" ? (
              row.title
            ) : (
              <span className="italic font-normal text-muted-foreground">
                (no title)
              </span>
            )}
          </div>
          <RowSupportLine row={row} />
          {isUnseen && row.newActivity !== null ? (
            <NewActivityLine activity={row.newActivity} rowId={row.id} />
          ) : null}
        </div>
        <RowMeta row={row} attention={attention} today={today} />
      </div>
      {expanded ? (
        <ExpandedRow
          row={row}
          viewerEmail={viewerEmail}
          actForOutsideSeats={actForOutsideSeats}
        />
      ) : null}
    </Card>
  );
}

/** Per-operator avatar colors — "who is this from" while scanning. Falls
 *  back to neutral for unknown actors (e.g. system writes). */
const ACTOR_AVATAR_CLASSES: Record<string, string> = {
  "chris.rose@legalflare.com": "bg-primary text-primary-foreground",
};

function ActorAvatar({ email }: { email: string }) {
  const operator = operatorByEmail(email);
  const display = operator?.displayName ?? email;
  const colorClass =
    ACTOR_AVATAR_CLASSES[email.trim().toLowerCase()] ??
    "bg-muted text-muted-foreground";
  return (
    <span
      className={cn(
        "mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold uppercase",
        colorClass,
      )}
      data-testid="row-actor"
      data-actor={email}
      title={display}
      aria-label={`From ${display}`}
    >
      {initials(display)}
    </span>
  );
}

/** Support line: entity (deep-linked when a detail surface exists) ·
 *  instruction preview. The card says what to do without expanding. */
function RowSupportLine({ row }: { row: DashboardRow }) {
  const instruction =
    row.instruction !== null && row.instruction.trim() !== ""
      ? row.instruction
      : null;
  if (row.aboutLabel === null && instruction === null) return null;
  return (
    <div className="flex min-w-0 items-baseline gap-1.5 text-[13px] text-muted-foreground">
      {row.aboutLabel !== null ? (
        row.aboutHref !== null ? (
          <Link
            to={row.aboutHref}
            onClick={(e) => e.stopPropagation()}
            className="shrink-0 underline decoration-muted-foreground/40 underline-offset-2 hover:text-foreground hover:decoration-foreground"
            data-testid={`row-about-link-${row.id}`}
          >
            {row.aboutLabel}
          </Link>
        ) : (
          <span className="shrink-0" data-testid={`row-about-${row.id}`}>
            {row.aboutLabel}
          </span>
        )
      ) : null}
      {row.aboutLabel !== null && instruction !== null ? (
        <span aria-hidden="true">·</span>
      ) : null}
      {instruction !== null ? (
        <span className="truncate" data-testid={`row-instruction-preview-${row.id}`}>
          {instruction}
        </span>
      ) : null}
    </div>
  );
}

/**
 * The "why is this new" line (ADR-0104) — the loudest element on an unread
 * card. A reply shows who said what ("Chris: …"); a fresh assignment
 * says who it's from; a status flip names the verb. All carry "how long
 * ago". Text + chip, not color alone (a11y).
 */
function NewActivityLine({
  activity,
  rowId,
}: {
  activity: NonNullable<DashboardRow["newActivity"]>;
  rowId: string;
}) {
  const name = operatorByEmail(activity.actor)?.displayName ?? activity.actor;
  const ago = fmtTimeAgo(activity.at);
  if (activity.kind === "message") {
    return (
      <div
        className="flex min-w-0 items-baseline gap-1.5 text-[13px]"
        data-testid={`row-new-message-${rowId}`}
      >
        <span className="shrink-0 rounded-full bg-cta px-1.5 py-0.5 text-[10px] font-semibold uppercase leading-none tracking-wide text-cta-foreground">
          New reply
        </span>
        <span className="min-w-0 truncate text-foreground">
          <span className="font-semibold">{name}:</span>{" "}
          {activity.snippet !== null ? `“${activity.snippet}”` : ""}
        </span>
        <span className="shrink-0 text-[11px] text-muted-foreground">
          {ago}
        </span>
      </div>
    );
  }
  const line =
    activity.kind === "status"
      ? `${name} ${activity.action === "send-back" ? "sent this back" : "marked this done"}`
      : `New from ${name}`;
  return (
    <div
      className="text-[13px] font-semibold text-cta"
      data-testid={`row-new-${activity.kind}-${rowId}`}
    >
      {line} · <span className="font-normal">{ago}</span>
    </div>
  );
}

/**
 * Right-side chips, max three and usually one (ADR-0104): the attention chip
 * (red/amber, reason named) OR a quiet humanized date chip; the snooze chip
 * when parked; a status chip only when status ≠ open. Thread count rides as
 * tiny text. Everything else (priority Normal/Low, OPEN, age, self-
 * assignment) earns no ink here — it's in the expand.
 */
function RowMeta({
  row,
  attention,
  today,
}: {
  row: DashboardRow;
  attention: Attention;
  today: string;
}) {
  const nearestDate =
    row.dueBy !== null
      ? { label: `Due ${fmtRelativeDay(row.dueBy, today)}` }
      : row.committedBy !== null
      ? { label: `Commit ${fmtRelativeDay(row.committedBy, today)}` }
      : null;
  return (
    <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5 text-xs text-muted-foreground">
      {attention.label !== null ? (
        <Badge
          variant="outline"
          className={cn(
            "border-transparent text-[10px] font-semibold uppercase tracking-wide",
            attention.level === "red"
              ? "bg-[hsl(var(--sev-red))] text-[hsl(var(--sev-ink))]"
              : "bg-[hsl(var(--sev-amber))] text-[hsl(var(--sev-ink))]",
          )}
          data-testid="row-attention"
          data-attention-level={attention.level}
        >
          {attention.label}
        </Badge>
      ) : nearestDate !== null ? (
        <Badge
          variant="outline"
          className="text-[10px]"
          data-testid="row-date"
        >
          {nearestDate.label}
        </Badge>
      ) : null}
      {row.isSnoozed ? (
        <Badge
          variant="outline"
          className="bg-info-bg text-[10px] text-interactive border-info-border"
          data-testid="row-snoozed-until"
        >
          Snoozed until {row.snoozedUntil}
        </Badge>
      ) : null}
      {row.status !== "open" ? (
        <Badge
          variant="outline"
          className={cn(
            "text-[10px] uppercase tracking-wider",
            row.status === "done" &&
              "border-success-border bg-success-bg text-success",
          )}
          data-testid="row-status"
        >
          {row.status}
        </Badge>
      ) : null}
      {row.threadCount > 0 ? (
        <span className="text-[11px]" data-testid="row-thread-count">
          {row.threadCount} msg{row.threadCount === 1 ? "" : "s"}
        </span>
      ) : null}
    </div>
  );
}

/**
 * Expanded row body — thread + add-message + seat-gated close-loop buttons.
 * Viewer who is neither assigner nor assignee sees the thread but no
 * action buttons (read-only). Mirrors the server seat guard for every
 * participant. The server also lets the system owner act from any seat
 * (#959); these buttons offer that only in the owner's outside-roster band
 * (`actForOutsideSeats`, #1024), and there only for the seats off the
 * roster — the seats no one else can act from. A watch there offers Un-pin.
 *
 * The tracking detail the collapsed card does not wear (ADR-0104) lives
 * here: provenance, assignment, both dates, priority, age.
 */
function ExpandedRow({
  row,
  viewerEmail,
  actForOutsideSeats = false,
}: {
  row: DashboardRow;
  viewerEmail: string;
  actForOutsideSeats?: boolean;
}) {
  const isWatch = row.assignee.trim() === "";
  const isAssignee =
    sameEmail(row.assignee, viewerEmail) ||
    (actForOutsideSeats && isOutsideRoster(row.assignee));
  const isAssigner =
    sameEmail(row.assigner, viewerEmail) ||
    (actForOutsideSeats && isOutsideRoster(row.assigner));
  const assignerDisplay =
    operatorByEmail(row.assigner)?.displayName ?? row.assigner;
  const assigneeDisplay =
    operatorByEmail(row.assignee)?.displayName ?? row.assignee;
  const detail = [
    `Created ${dayOf(row.created)} by ${assignerDisplay}`,
    isWatch
      ? `Watch held by ${assignerDisplay}`
      : `${assignerDisplay}→${assigneeDisplay}`,
    `${ageLabel(daysSince(dayOf(row.created), todayInPT()))} old`,
    ...(row.dueBy !== null ? [`Due ${row.dueBy}`] : []),
    ...(row.committedBy !== null ? [`Commit ${row.committedBy}`] : []),
    `${row.priority} priority`,
    `Status: ${row.status}`,
  ].join(" · ");
  return (
    <div
      className="border-t border-border px-3 pb-3 pt-2"
      data-testid={`dashboard-row-${row.id}-expanded`}
    >
      <div
        className="mb-2 text-[11px] text-muted-foreground"
        data-testid={`row-provenance-${row.id}`}
      >
        {detail}
      </div>
      {row.instruction !== null && row.instruction.trim() !== "" ? (
        <div
          className="mb-3 whitespace-pre-wrap text-sm text-foreground"
          data-testid={`row-instruction-${row.id}`}
        >
          {row.instruction}
        </div>
      ) : null}
      <Thread taskId={row.id} />
      <AddMessageForm taskId={row.id} />
      <div className="mt-3 flex flex-wrap gap-2" data-testid="row-actions">
        {isWatch && isAssigner ? <UnpinAction row={row} /> : null}
        {!isWatch && isAssignee ? <AssigneeActions row={row} /> : null}
        {!isWatch && isAssigner ? <AssignerActions row={row} /> : null}
        {!isAssignee && !isAssigner ? (
          <span
            className="text-xs italic text-muted-foreground"
            data-testid="row-read-only"
          >
            You're not on this to-do — view-only.
          </span>
        ) : null}
      </div>
    </div>
  );
}

function Thread({ taskId }: { taskId: string }) {
  const query = useTaskMessages(taskId, true);
  if (query.isLoading) {
    return (
      <div className="text-xs text-muted-foreground" data-testid="thread-loading">
        Loading thread…
      </div>
    );
  }
  if (query.isError) {
    return (
      <div className="text-xs text-error" data-testid="thread-error">
        Couldn't load the thread.
      </div>
    );
  }
  const messages = query.data?.messages ?? [];
  if (messages.length === 0) {
    return (
      <div
        className="text-xs italic text-muted-foreground"
        data-testid="thread-empty"
      >
        No messages yet.
      </div>
    );
  }
  return (
    <ol className="space-y-2" data-testid="thread">
      {messages.map((m) => (
        <ThreadMessage key={m.id} message={m} />
      ))}
    </ol>
  );
}

function ThreadMessage({ message }: { message: TaskMessage }) {
  const authorDisplay =
    operatorByEmail(message.author)?.displayName ?? message.author;
  return (
    <li
      className="rounded border border-border bg-muted/30 p-2 text-xs"
      data-testid={`thread-message-${message.id}`}
    >
      <div className="mb-1 flex items-center justify-between text-muted-foreground">
        <span className="font-medium">{authorDisplay}</span>
        <span title={message.timestamp}>{fmtTimeAgo(message.timestamp)}</span>
      </div>
      <div className="whitespace-pre-wrap text-foreground">{message.body}</div>
    </li>
  );
}

function AddMessageForm({ taskId }: { taskId: string }) {
  const [body, setBody] = useState("");
  const mutation = useAddMessage(taskId);
  function submit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = body.trim();
    if (trimmed === "") return;
    mutation.mutate(
      { body: trimmed },
      { onSuccess: () => setBody("") },
    );
  }
  return (
    <form
      onSubmit={submit}
      className="mt-3 space-y-2"
      data-testid="add-message-form"
    >
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Add a message to the thread…"
        rows={2}
        className="w-full rounded border border-border bg-background p-2 text-xs"
        data-testid="add-message-body"
      />
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={body.trim() === "" || mutation.isPending}
          className="rounded bg-foreground px-2 py-1 text-xs text-background disabled:opacity-40"
          data-testid="add-message-submit"
        >
          Post message
        </button>
      </div>
    </form>
  );
}

/**
 * Assignee close-loop buttons — Mark done (with optional write-up textarea)
 * and Set commit (date picker). Each button reveals its own inline form;
 * Submit fires the mutation, which on success collapses the form via
 * pending-action reset.
 */
function AssigneeActions({ row }: { row: DashboardRow }) {
  const [active, setActive] = useState<
    "mark-done" | "set-commit" | "snooze" | null
  >(null);
  const action = useTaskAction();
  const isSnoozed = row.isSnoozed;

  function reset() {
    setActive(null);
  }

  if (active === "snooze") {
    return (
      <SnoozeForm
        row={row}
        pending={action.isPending}
        onCancel={reset}
        onSubmit={(snoozedUntil) =>
          action.mutate(
            { id: row.id, action: "snooze", snoozedUntil } as Parameters<
              typeof action.mutate
            >[0],
            { onSuccess: reset },
          )
        }
      />
    );
  }

  if (active === "mark-done") {
    return (
      <MarkDoneForm
        row={row}
        pending={action.isPending}
        onCancel={reset}
        onSubmit={(note) =>
          action.mutate(
            {
              id: row.id,
              action: "mark-done",
              ...(note ? { note } : {}),
            },
            { onSuccess: reset },
          )
        }
      />
    );
  }
  if (active === "set-commit") {
    return (
      <SetCommitForm
        row={row}
        pending={action.isPending}
        onCancel={reset}
        onSubmit={(committedBy) =>
          action.mutate(
            { id: row.id, action: "set-commit", committedBy } as Parameters<
              typeof action.mutate
            >[0],
            { onSuccess: reset },
          )
        }
      />
    );
  }

  return (
    <>
      <ActionButton
        kind="primary"
        onClick={() => setActive("mark-done")}
        testid="btn-mark-done"
      >
        Mark done
      </ActionButton>
      <ActionButton
        kind="secondary"
        onClick={() => setActive("set-commit")}
        testid="btn-set-commit"
      >
        Set commit
      </ActionButton>
      {isSnoozed ? (
        <ActionButton
          kind="ghost"
          onClick={() =>
            action.mutate({ id: row.id, action: "unsnooze" })
          }
          testid="btn-unsnooze"
        >
          Unsnooze
        </ActionButton>
      ) : (
        <ActionButton
          kind="ghost"
          onClick={() => setActive("snooze")}
          testid="btn-snooze"
        >
          Snooze
        </ActionButton>
      )}
    </>
  );
}

/**
 * Snooze form — assignee picks a date the row should re-surface. Mirrors
 * SetCommitForm's shape (single date input). Slice H (ADR-0088).
 *
 * Empty default: never pre-fill with `row.snoozedUntil`. A re-surfaced row
 * carries yesterday's date; pre-filling would let the operator save it
 * unchanged and produce a snooze that hides nothing. `min={todayInPT()}`
 * keeps the picker honest at the browser layer.
 */
function SnoozeForm({
  row,
  pending,
  onCancel,
  onSubmit,
}: {
  row: DashboardRow;
  pending: boolean;
  onCancel: () => void;
  onSubmit: (snoozedUntil: string) => void;
}) {
  const [date, setDate] = useState("");
  const today = todayInPT();
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (date.trim() === "") return;
        onSubmit(date);
      }}
      className="w-full space-y-2 rounded border border-border bg-muted/30 p-2"
      data-testid={`snooze-form-${row.id}`}
    >
      <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        Snooze until
      </div>
      <input
        type="date"
        value={date}
        min={today}
        onChange={(e) => setDate(e.target.value)}
        className="rounded border border-border bg-background p-1 text-xs"
        data-testid="snooze-date"
      />
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded px-2 py-1 text-xs text-muted-foreground"
          data-testid="snooze-form-cancel"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={pending || date.trim() === ""}
          className="rounded bg-foreground px-2 py-1 text-xs text-background disabled:opacity-40"
          data-testid="snooze-submit"
        >
          Save snooze
        </button>
      </div>
    </form>
  );
}

/**
 * Assigner close-loop buttons — Acknowledge (status === "done" only), Send
 * back (status === "done" only), Cancel (any status). Acknowledge and Cancel
 * both hit the wire `clear` action; the UI verb disambiguates intent in the
 * thread message. The "Acknowledge" verb (renamed from "Confirm" in Slice J
 * / ADR-0092) signals that a done row is the assigner's to consume — same
 * shape the pile-at-top section header uses.
 */
/** A watch's one verb — Un-pin, the Surface's `clear` (pinned-card.tsx).
 *  Reached only from the owner's outside-roster band (#1024). */
function UnpinAction({ row }: { row: DashboardRow }) {
  const action = useTaskAction();
  return (
    <ActionButton
      kind="ghost"
      onClick={() => action.mutate({ id: row.id, action: "clear" })}
      testid="btn-unpin"
    >
      Un-pin
    </ActionButton>
  );
}

function AssignerActions({ row }: { row: DashboardRow }) {
  const [active, setActive] = useState<
    "acknowledge" | "send-back" | "cancel" | null
  >(null);
  const action = useTaskAction();

  function reset() {
    setActive(null);
  }

  if (active === "acknowledge") {
    return (
      <ClearForm
        row={row}
        verb="Acknowledge"
        pending={action.isPending}
        notePlaceholder="Optional note for the thread"
        onCancel={reset}
        onSubmit={(note) =>
          action.mutate(
            {
              id: row.id,
              action: "clear",
              ...(note ? { note } : {}),
            },
            { onSuccess: reset },
          )
        }
      />
    );
  }
  if (active === "cancel") {
    return (
      <ClearForm
        row={row}
        verb="Cancel to-do"
        pending={action.isPending}
        notePlaceholder="Optional note — why is this being cancelled?"
        onCancel={reset}
        onSubmit={(note) =>
          action.mutate(
            {
              id: row.id,
              action: "clear",
              ...(note ? { note } : {}),
            },
            { onSuccess: reset },
          )
        }
      />
    );
  }
  if (active === "send-back") {
    return (
      <SendBackForm
        row={row}
        pending={action.isPending}
        onCancel={reset}
        onSubmit={(note) =>
          action.mutate(
            {
              id: row.id,
              action: "send-back",
              ...(note ? { note } : {}),
            },
            { onSuccess: reset },
          )
        }
      />
    );
  }

  const done = row.status === "done";
  return (
    <>
      {done ? (
        <ActionButton
          kind="primary"
          onClick={() => setActive("acknowledge")}
          testid="btn-acknowledge"
        >
          Acknowledge
        </ActionButton>
      ) : null}
      {done ? (
        <ActionButton
          kind="secondary"
          onClick={() => setActive("send-back")}
          testid="btn-send-back"
        >
          Send back
        </ActionButton>
      ) : null}
      <ActionButton
        kind="ghost"
        onClick={() => setActive("cancel")}
        testid="btn-cancel"
      >
        Cancel
      </ActionButton>
    </>
  );
}

function MarkDoneForm({
  row,
  pending,
  onCancel,
  onSubmit,
}: {
  row: DashboardRow;
  pending: boolean;
  onCancel: () => void;
  onSubmit: (note: string) => void;
}) {
  const [note, setNote] = useState("");
  return (
    <NoteForm
      title="Mark done"
      note={note}
      setNote={setNote}
      placeholder="Optional write-up — what did you do?"
      submitLabel="Mark done"
      testidPrefix="mark-done"
      pending={pending}
      onCancel={onCancel}
      onSubmit={() => onSubmit(note.trim())}
      rowId={row.id}
    />
  );
}

function SendBackForm({
  row,
  pending,
  onCancel,
  onSubmit,
}: {
  row: DashboardRow;
  pending: boolean;
  onCancel: () => void;
  onSubmit: (note: string) => void;
}) {
  const [note, setNote] = useState("");
  return (
    <NoteForm
      title="Send back"
      note={note}
      setNote={setNote}
      placeholder="Reason — what needs more work?"
      submitLabel="Send back"
      testidPrefix="send-back"
      pending={pending}
      onCancel={onCancel}
      onSubmit={() => onSubmit(note.trim())}
      rowId={row.id}
    />
  );
}

function ClearForm({
  row,
  verb,
  pending,
  notePlaceholder,
  onCancel,
  onSubmit,
}: {
  row: DashboardRow;
  verb: string;
  pending: boolean;
  notePlaceholder: string;
  onCancel: () => void;
  onSubmit: (note: string) => void;
}) {
  const [note, setNote] = useState("");
  return (
    <NoteForm
      title={verb}
      note={note}
      setNote={setNote}
      placeholder={notePlaceholder}
      submitLabel={verb}
      testidPrefix={verb === "Acknowledge" ? "acknowledge" : "cancel"}
      pending={pending}
      onCancel={onCancel}
      onSubmit={() => onSubmit(note.trim())}
      rowId={row.id}
    />
  );
}

function NoteForm({
  title,
  note,
  setNote,
  placeholder,
  submitLabel,
  testidPrefix,
  pending,
  onCancel,
  onSubmit,
  rowId,
}: {
  title: string;
  note: string;
  setNote: (v: string) => void;
  placeholder: string;
  submitLabel: string;
  testidPrefix: string;
  pending: boolean;
  onCancel: () => void;
  onSubmit: () => void;
  rowId: string;
}) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="w-full space-y-2 rounded border border-border bg-muted/30 p-2"
      data-testid={`${testidPrefix}-form-${rowId}`}
    >
      <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {title}
      </div>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder={placeholder}
        rows={3}
        className="w-full rounded border border-border bg-background p-2 text-xs"
        data-testid={`${testidPrefix}-note`}
      />
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded px-2 py-1 text-xs text-muted-foreground"
          data-testid={`${testidPrefix}-form-cancel`}
        >
          Back
        </button>
        <button
          type="submit"
          disabled={pending}
          className="rounded bg-foreground px-2 py-1 text-xs text-background disabled:opacity-40"
          data-testid={`${testidPrefix}-submit`}
        >
          {submitLabel}
        </button>
      </div>
    </form>
  );
}

function SetCommitForm({
  row,
  pending,
  onCancel,
  onSubmit,
}: {
  row: DashboardRow;
  pending: boolean;
  onCancel: () => void;
  onSubmit: (committedBy: string) => void;
}) {
  const [date, setDate] = useState(row.committedBy ?? "");
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (date.trim() === "") return;
        onSubmit(date);
      }}
      className="w-full space-y-2 rounded border border-border bg-muted/30 p-2"
      data-testid={`set-commit-form-${row.id}`}
    >
      <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        Set commit
      </div>
      <input
        type="date"
        value={date}
        onChange={(e) => setDate(e.target.value)}
        className="rounded border border-border bg-background p-1 text-xs"
        data-testid="set-commit-date"
      />
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded px-2 py-1 text-xs text-muted-foreground"
          data-testid="set-commit-form-cancel"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={pending || date.trim() === ""}
          className="rounded bg-foreground px-2 py-1 text-xs text-background disabled:opacity-40"
          data-testid="set-commit-submit"
        >
          Save commit
        </button>
      </div>
    </form>
  );
}

function ActionButton({
  kind,
  onClick,
  testid,
  children,
}: {
  kind: "primary" | "secondary" | "ghost";
  onClick: () => void;
  testid: string;
  children: React.ReactNode;
}) {
  const base = "rounded px-2 py-1 text-xs";
  const variant =
    kind === "primary"
      ? "bg-foreground text-background"
      : kind === "secondary"
      ? "border border-border text-foreground"
      : "text-muted-foreground hover:text-foreground";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${base} ${variant}`}
      data-testid={testid}
    >
      {children}
    </button>
  );
}
